import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Typography, TextField, MenuItem, ListSubheader, Switch, FormControlLabel, Divider, Chip, Stack, Button, Autocomplete } from '@mui/material';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AddLinkIcon from '@mui/icons-material/AddLinkOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/api/axiosClient';
import { DEFAULT_AUTO_GENERATE_CONFIG, formatSequenceValue } from '@/lib/autoGenerate';
import { GRN_LABEL_FIELDS, LABEL_FIELDS_BY_CONTEXT, MATERIAL_LABEL_FIELDS, PRODUCTION_LABEL_FIELDS, SUPPLIER_LABEL_FIELDS, SYSTEM_LABEL_FIELDS } from '@/lib/labelFields';
import type { AutoGenerateConfig, BarcodeSymbology, LabelObject, LabelTemplate, LogoObject, QrErrorCorrection, TableObject } from '@/types/labelTemplate';
import type { MaterialDto, PagedResult } from '@/types/materials';

const MAX_LOGO_FILE_BYTES = 2 * 1024 * 1024;

// A Packing-assigned template only ever prints on the Packing Label Print screen (see
// LabelTemplate.AssignmentType), so it only gets Packing's own field pool — offering it
// GRN/Production fields like {{GrnNumber}} would just be a binding with no real data behind it,
// the same anti-pattern LABEL_FIELDS_BY_CONTEXT's own doc comment warns against. Generic/Material
// templates keep the merged GRN+Production pool (dedup'd by key) since either print screen might
// use them.
//
// Built from the individual field lists directly (not LABEL_FIELDS_BY_CONTEXT.grn/.production,
// which bundle Material at the very top of grn's own list) so the merged order here can be
// Supplier -> Grn -> Material -> Production -> System instead — Material Code/Name sit in their
// own 'Material' group positioned directly between Grn and Production, one scroll away from
// either heading, rather than buried at the top past Supplier or duplicated (MUI's Autocomplete
// options are flat strings — the same key can't appear twice under two different group labels;
// .groupBy's lookup always resolves to whichever occurrence comes first, so a second copy would
// just silently render under the same group as the first, not a distinct one). They resolve to
// the exact same {{MaterialCode}}/{{MaterialName}} token regardless of which heading they're
// found under. LABEL_FIELDS_BY_CONTEXT.grn/.production themselves are untouched, so the actual
// GRN/Production Label Print screens' own field grouping is unaffected.
function bindableFieldsFor(assignmentType: LabelTemplate['assignmentType']) {
  if (assignmentType === 'Packing') return LABEL_FIELDS_BY_CONTEXT.packing;
  if (assignmentType === 'WeightProduction') return LABEL_FIELDS_BY_CONTEXT.weightProduction;
  const productionOnly = PRODUCTION_LABEL_FIELDS.filter((f) => !GRN_LABEL_FIELDS.some((g) => g.key === f.key));
  return [
    ...SUPPLIER_LABEL_FIELDS,
    ...GRN_LABEL_FIELDS,
    ...MATERIAL_LABEL_FIELDS,
    ...productionOnly,
    ...SYSTEM_LABEL_FIELDS,
  ];
}

/** Renders a flat field list as MenuItems with a ListSubheader before each group, in first-seen
 * group order — used by the plain `<TextField select>` field binder below. */
function groupedFieldMenuItems(fields: { key: string; group: string }[]) {
  const groups = [...new Set(fields.map((f) => f.group))];
  return groups.flatMap((group) => [
    <ListSubheader key={`group-${group}`}>{group}</ListSubheader>,
    ...fields
      .filter((f) => f.group === group)
      .map((f) => (
        <MenuItem key={f.key} value={f.key}>
          {f.key}
        </MenuItem>
      )),
  ]);
}

/** Scopes a template to exactly one Material, marks it Generic (any material), or marks it as a
 * Packing-only template — Label Printing screens only offer a scoped template once that specific
 * Material is selected, and only Packing Label Print offers a Packing-type template (GRN/
 * Production never do, even if Active). */
function TemplateAssignment({ template, onTemplateMetaChange }: { template: LabelTemplate; onTemplateMetaChange: (patch: Partial<LabelTemplate>) => void }) {
  const { t } = useTranslation('labelDesigner');
  const { data: materialsPage } = useQuery({
    queryKey: ['materials', 'all-for-picker'],
    queryFn: async () => (await apiClient.get<PagedResult<MaterialDto>>('/materials', { params: { page: 1, pageSize: 200 } })).data,
  });
  const materials = materialsPage?.items ?? [];

  const assignTo =
    template.assignmentType === 'Packing' ? 'packing' :
    template.assignmentType === 'WeightProduction' ? 'weightProduction' :
    template.materialId ? 'material' : 'none';
  const isScoped = assignTo === 'material';

  return (
    <Stack spacing={1.5}>
      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {t('properties.assignment.title')}
      </Typography>
      <TextField
        select
        label={t('properties.assignment.assignTo')}
        size="small"
        fullWidth
        value={assignTo}
        onChange={(e) => {
          const next = e.target.value;
          if (next === 'material') {
            onTemplateMetaChange({ assignmentType: 'Material', materialId: materials[0]?.id ?? null });
          } else if (next === 'packing') {
            onTemplateMetaChange({ assignmentType: 'Packing', materialId: null });
          } else if (next === 'weightProduction') {
            onTemplateMetaChange({ assignmentType: 'WeightProduction', materialId: null });
          } else {
            onTemplateMetaChange({ assignmentType: 'Generic', materialId: null });
          }
        }}
      >
        <MenuItem value="none">{t('properties.assignment.generic')}</MenuItem>
        <MenuItem value="material">{t('properties.assignment.material')}</MenuItem>
        <MenuItem value="packing">{t('properties.assignment.packing')}</MenuItem>
        <MenuItem value="weightProduction">{t('properties.assignment.weightProduction')}</MenuItem>
      </TextField>
      {isScoped && (
        <TextField select label={t('properties.assignment.material')} size="small" fullWidth value={template.materialId ?? ''} onChange={(e) => onTemplateMetaChange({ materialId: e.target.value })}>
          {materials.map((m) => (
            <MenuItem key={m.id} value={m.id}>
              {m.code} — {m.name}
            </MenuItem>
          ))}
        </TextField>
      )}
      <Typography variant="caption" color="text.secondary">
        {assignTo === 'packing' ? t('properties.assignment.packingHelp') : assignTo === 'weightProduction' ? t('properties.assignment.weightProductionHelp') : isScoped ? t('properties.assignment.scopedHelp') : t('properties.assignment.genericHelp')}
      </Typography>
    </Stack>
  );
}

const BARCODE_SYMBOLOGIES: BarcodeSymbology[] = ['CODE128', 'CODE39', 'EAN13', 'UPC', 'PDF417'];
const ERROR_CORRECTIONS: QrErrorCorrection[] = ['L', 'M', 'Q', 'H'];

/** Field picker that inserts a {{FieldName}} token — the manual alternative to remembering exact field names by hand. */
function MaterialFieldBinder({ object, set, assignmentType }: { object: LabelObject; set: (patch: Record<string, unknown>) => void; assignmentType: LabelTemplate['assignmentType'] }) {
  const { t } = useTranslation('labelDesigner');
  const [field, setField] = useState('');
  const bindableFields = bindableFieldsFor(assignmentType);

  // Start over whenever a different object is selected, so a stale field selection from the last
  // object never lingers.
  useEffect(() => {
    setField('');
  }, [object.id]);

  const applyBinding = () => {
    if (!field) return;
    const token = `{{${field}}}`;
    if (object.type === 'text') {
      // Inserting a field token and auto-generating are mutually exclusive too — see the
      // barcode/qr branch below for why.
      set({ text: `${object.text || ''}${token}`, autoGenerate: undefined });
    } else if (object.type === 'barcode' || object.type === 'qr') {
      // Barcode/QR data is bound to exactly one field — replace, don't concatenate. Binding to a
      // Material field and auto-generating are mutually exclusive, so clear any auto-gen config.
      set({ data: token, autoGenerate: undefined });
    }
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {t('properties.bindMaterialField')}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {t('properties.fieldsScope', { scope: t(`properties.assignment.${assignmentType === 'Packing' ? 'packing' : assignmentType === 'WeightProduction' ? 'weightProduction' : assignmentType === 'Material' ? 'material' : 'generic'}`) })}
      </Typography>
      <TextField select label={t('properties.field')} size="small" fullWidth value={field} onChange={(e) => setField(e.target.value)}>
        {groupedFieldMenuItems(bindableFields)}
      </TextField>
      <Button size="small" variant="outlined" startIcon={<AddLinkIcon fontSize="small" />} disabled={!field} onClick={applyBinding}>
        {object.type === 'text' ? t('properties.insertField') : t('properties.setAsDataSource')}
      </Button>
    </Stack>
  );
}

/** Lets the designer pick an image file from their system for a Logo/Image object — stored as a data URI directly in the template's CanvasJson, same as every other object property. */
function LogoUploader({ object, set }: { object: LogoObject; set: (patch: Record<string, unknown>) => void }) {
  const { t } = useTranslation('labelDesigner');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = (file: File) => {
    if (file.size > MAX_LOGO_FILE_BYTES) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => set({ src: reader.result as string });
    reader.readAsDataURL(file);
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {t('properties.logoImage')}
      </Typography>
      {object.src && (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, display: 'flex', justifyContent: 'center', bgcolor: 'action.hover' }}>
          <Box component="img" src={object.src} alt="" sx={{ maxWidth: '100%', maxHeight: 120 }} />
        </Box>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
          e.target.value = '';
        }}
      />
      <Stack direction="row" spacing={1}>
        <Button size="small" variant="outlined" startIcon={<FileUploadOutlinedIcon fontSize="small" />} onClick={() => inputRef.current?.click()} fullWidth>
          {object.src ? t('properties.replaceImage') : t('properties.uploadImage')}
        </Button>
        {object.src && (
          <Button size="small" color="error" variant="outlined" onClick={() => set({ src: undefined })}>
            {t('properties.removeImage')}
          </Button>
        )}
      </Stack>
    </Stack>
  );
}

/** Lets the template designer replace a barcode/QR's data source with a server-issued, never-repeating counter — one unique value per physical label printed, not per print job. */
function AutoGenerateFields({ config, set }: { config: AutoGenerateConfig; set: (patch: Record<string, unknown>) => void }) {
  const { t } = useTranslation('labelDesigner');
  const update = (patch: Partial<AutoGenerateConfig>) => set({ autoGenerate: { ...config, ...patch } });

  return (
    <Stack spacing={1.5}>
      <GridContainer columns={12} spacing={1.5}>
        <GridItem xs={6}>
          <TextField label={t('properties.autoGenerate.prefix')} size="small" fullWidth value={config.prefix} onChange={(e) => update({ prefix: e.target.value })} />
        </GridItem>
        <GridItem xs={6}>
          <TextField label={t('properties.autoGenerate.suffix')} size="small" fullWidth value={config.suffix} onChange={(e) => update({ suffix: e.target.value })} />
        </GridItem>
        <GridItem xs={6}>
          <TextField
            label={t('properties.autoGenerate.startValue')}
            type="number"
            size="small"
            fullWidth
            value={config.startValue}
            onChange={(e) => update({ startValue: Number(e.target.value) })}
          />
        </GridItem>
        <GridItem xs={6}>
          <TextField
            label={t('properties.autoGenerate.increment')}
            type="number"
            size="small"
            fullWidth
            value={config.increment}
            onChange={(e) => update({ increment: Number(e.target.value) })}
          />
        </GridItem>
        <GridItem xs={6}>
          <TextField
            label={t('properties.autoGenerate.padding')}
            type="number"
            size="small"
            fullWidth
            value={config.padding}
            onChange={(e) => update({ padding: Number(e.target.value) })}
          />
        </GridItem>
      </GridContainer>
      <Typography variant="caption" color="text.secondary">
        {t('properties.autoGenerate.previewPrefix')} <strong>{formatSequenceValue(config, config.startValue)}</strong>. {t('properties.autoGenerate.previewSuffix')}
      </Typography>
    </Stack>
  );
}

/** Field-picker for the Table object — one selectable pool per template (see bindableFieldsFor),
 * stacked as "Label: Value" rows. A Packing-assigned template's pool already includes every
 * Packing/Package field (PackageNo, Qty, Weight, ...) alongside Material/System fields in the same
 * list, so there's no separate "table type" to choose. */
function TableFieldsEditor({ object, set, assignmentType }: { object: TableObject; set: (patch: Record<string, unknown>) => void; assignmentType: LabelTemplate['assignmentType'] }) {
  const { t } = useTranslation('labelDesigner');
  const fieldOptions = bindableFieldsFor(assignmentType);

  return (
    <Stack spacing={1.5}>
      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {t('properties.tableFields')}
      </Typography>
      <Autocomplete
        multiple
        size="small"
        options={fieldOptions.map((f) => f.key)}
        groupBy={(option) => fieldOptions.find((f) => f.key === option)?.group ?? ''}
        value={object.fields}
        onChange={(_, values) => set({ fields: values })}
        renderInput={(params) => <TextField {...params} label={t('properties.tableFieldsPicker')} />}
      />
      <GridContainer columns={12} spacing={1.5}>
        <GridItem xs={6}>
          <TextField
            label={t('properties.rowHeight')}
            type="number"
            size="small"
            fullWidth
            value={object.rowHeight}
            onChange={(e) => set({ rowHeight: Number(e.target.value) })}
          />
        </GridItem>
        <GridItem xs={6}>
          <TextField
            label={t('properties.fontSize')}
            type="number"
            size="small"
            fullWidth
            value={object.fontSize}
            onChange={(e) => set({ fontSize: Number(e.target.value) })}
          />
        </GridItem>
      </GridContainer>
      <FormControlLabel
        control={<Switch checked={object.showLabels} onChange={(e) => set({ showLabels: e.target.checked })} />}
        label={t('properties.tableShowLabels')}
      />
    </Stack>
  );
}

interface PropertiesPanelProps {
  object: LabelObject | null;
  onChange: (id: string, patch: Record<string, unknown>) => void;
  onDelete: (id: string) => void;
  template: LabelTemplate;
  onTemplateMetaChange: (patch: Partial<LabelTemplate>) => void;
}

export default function PropertiesPanel({ object, onChange, onDelete, template, onTemplateMetaChange }: PropertiesPanelProps) {
  const { t } = useTranslation('labelDesigner');

  if (!object) {
    return (
      <Box sx={{ width: 320, borderLeft: '1px solid', borderColor: 'divider', p: 2.5, overflowY: 'auto' }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          {t('properties.templateSetup')}
        </Typography>
        <Stack spacing={2}>
          <TextField label={t('properties.templateName')} fullWidth size="small" value={template.name} onChange={(e) => onTemplateMetaChange({ name: e.target.value })} />
          <GridContainer columns={12} spacing={1.5}>
            <GridItem xs={6}>
              <TextField label={t('properties.width')} type="number" fullWidth size="small" value={template.widthMm} onChange={(e) => onTemplateMetaChange({ widthMm: Number(e.target.value) })} />
            </GridItem>
            <GridItem xs={6}>
              <TextField
                label={t('properties.height')}
                type="number"
                fullWidth
                size="small"
                value={template.heightMm}
                onChange={(e) => onTemplateMetaChange({ heightMm: Number(e.target.value) })}
              />
            </GridItem>
          </GridContainer>
          <TextField
            label={t('properties.description')}
            fullWidth
            size="small"
            multiline
            minRows={2}
            value={template.description}
            onChange={(e) => onTemplateMetaChange({ description: e.target.value })}
          />
        </Stack>
        <Divider sx={{ my: 3 }} />
        <TemplateAssignment template={template} onTemplateMetaChange={onTemplateMetaChange} />
        <Divider sx={{ my: 3 }} />
        <Typography variant="body2" color="text.secondary">
          {t('properties.selectPrompt')}
        </Typography>
      </Box>
    );
  }

  const set = (patch: Record<string, unknown>) => onChange(object.id, patch);

  return (
    <Box sx={{ width: 320, borderLeft: '1px solid', borderColor: 'divider', p: 2.5, overflowY: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle2">{t('properties.properties')}</Typography>
        <Chip label={object.type.toUpperCase()} size="small" color="primary" variant="outlined" />
      </Stack>

      <Stack spacing={2}>
        <GridContainer columns={12} spacing={1.5}>
          <GridItem xs={6}>
            <TextField label={t('properties.x')} type="number" size="small" fullWidth value={Math.round(object.x)} onChange={(e) => set({ x: Number(e.target.value) })} />
          </GridItem>
          <GridItem xs={6}>
            <TextField label={t('properties.y')} type="number" size="small" fullWidth value={Math.round(object.y)} onChange={(e) => set({ y: Number(e.target.value) })} />
          </GridItem>
          <GridItem xs={6}>
            <TextField label={t('properties.objWidth')} type="number" size="small" fullWidth value={Math.round(object.w)} onChange={(e) => set({ w: Number(e.target.value) })} />
          </GridItem>
          <GridItem xs={6}>
            <TextField label={t('properties.objHeight')} type="number" size="small" fullWidth value={Math.round(object.h)} onChange={(e) => set({ h: Number(e.target.value) })} />
          </GridItem>
        </GridContainer>

        <Divider />

        {(object.type === 'text' || object.type === 'barcode' || object.type === 'qr') && (
          <>
            <MaterialFieldBinder object={object} set={set} assignmentType={template.assignmentType} />
            <Divider />
          </>
        )}

        {object.type === 'text' && (
          <>
            <FormControlLabel
              control={
                <Switch
                  checked={!!object.autoGenerate}
                  onChange={(e) => set({ autoGenerate: e.target.checked ? DEFAULT_AUTO_GENERATE_CONFIG : undefined, text: '' })}
                />
              }
              label={t('properties.autoGenerateToggle')}
            />
            {object.autoGenerate ? (
              <AutoGenerateFields config={object.autoGenerate} set={set} />
            ) : (
              <TextField
                label={t('properties.textPlaceholder')}
                fullWidth
                size="small"
                multiline
                minRows={2}
                value={object.text}
                onChange={(e) => set({ text: e.target.value })}
                helperText={t('properties.tokenHelperText')}
              />
            )}
            <GridContainer columns={12} spacing={1.5}>
              <GridItem xs={6}>
                <TextField label={t('properties.fontSize')} type="number" size="small" fullWidth value={object.fontSize} onChange={(e) => set({ fontSize: Number(e.target.value) })} />
              </GridItem>
              <GridItem xs={6}>
                <TextField select label={t('properties.align')} size="small" fullWidth value={object.align || 'left'} onChange={(e) => set({ align: e.target.value })}>
                  <MenuItem value="left">{t('properties.alignLeft')}</MenuItem>
                  <MenuItem value="center">{t('properties.alignCenter')}</MenuItem>
                  <MenuItem value="right">{t('properties.alignRight')}</MenuItem>
                </TextField>
              </GridItem>
            </GridContainer>
            <FormControlLabel
              control={<Switch checked={object.fontWeight === 700} onChange={(e) => set({ fontWeight: e.target.checked ? 700 : 400 })} />}
              label={t('properties.bold')}
            />
          </>
        )}

        {object.type === 'barcode' && (
          <>
            <TextField select label={t('properties.symbology')} size="small" fullWidth value={object.symbology} onChange={(e) => set({ symbology: e.target.value })}>
              {BARCODE_SYMBOLOGIES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={!!object.autoGenerate}
                  onChange={(e) => set({ autoGenerate: e.target.checked ? DEFAULT_AUTO_GENERATE_CONFIG : undefined })}
                />
              }
              label={t('properties.autoGenerateToggle')}
            />
            {object.autoGenerate ? (
              <AutoGenerateFields config={object.autoGenerate} set={set} />
            ) : (
              <TextField
                label={t('properties.dataSource')}
                fullWidth
                size="small"
                value={object.data}
                onChange={(e) => set({ data: e.target.value })}
                helperText={t('properties.tokenHelperText')}
              />
            )}
            <FormControlLabel control={<Switch checked={!!object.humanReadable} onChange={(e) => set({ humanReadable: e.target.checked })} />} label={t('properties.humanReadable')} />
          </>
        )}

        {object.type === 'qr' && (
          <>
            <FormControlLabel
              control={
                <Switch
                  checked={!!object.autoGenerate}
                  onChange={(e) => set({ autoGenerate: e.target.checked ? DEFAULT_AUTO_GENERATE_CONFIG : undefined })}
                />
              }
              label={t('properties.autoGenerateToggle')}
            />
            {object.autoGenerate ? (
              <AutoGenerateFields config={object.autoGenerate} set={set} />
            ) : (
              <TextField
                label={t('properties.dataSource')}
                fullWidth
                size="small"
                value={object.data}
                onChange={(e) => set({ data: e.target.value })}
                helperText={t('properties.tokenHelperText')}
              />
            )}
            <TextField select label={t('properties.errorCorrection')} size="small" fullWidth value={object.errorCorrection || 'M'} onChange={(e) => set({ errorCorrection: e.target.value })}>
              {ERROR_CORRECTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          </>
        )}

        {object.type === 'rectangle' && (
          <TextField select label={t('properties.shape')} size="small" fullWidth value={object.shape || 'rectangle'} onChange={(e) => set({ shape: e.target.value })}>
            <MenuItem value="rectangle">{t('properties.shapeRectangle')}</MenuItem>
            <MenuItem value="ellipse">{t('properties.shapeEllipse')}</MenuItem>
          </TextField>
        )}

        {(object.type === 'rectangle' || object.type === 'line') && (
          <GridContainer columns={12} spacing={1.5}>
            <GridItem xs={6}>
              <TextField label={t('properties.borderWidth')} type="number" size="small" fullWidth value={object.borderWidth || 2} onChange={(e) => set({ borderWidth: Number(e.target.value) })} />
            </GridItem>
            <GridItem xs={6}>
              <TextField label={t('properties.borderColor')} type="color" size="small" fullWidth value={object.borderColor || '#414752'} onChange={(e) => set({ borderColor: e.target.value })} />
            </GridItem>
          </GridContainer>
        )}

        {(object.type === 'logo' || object.type === 'image') && <LogoUploader object={object} set={set} />}

        {object.type === 'table' && <TableFieldsEditor object={object} set={set} assignmentType={template.assignmentType} />}

        <Divider />
        <Button color="error" variant="outlined" startIcon={<DeleteOutlineIcon />} onClick={() => onDelete(object.id)}>
          {t('properties.deleteObject')}
        </Button>
      </Stack>
    </Box>
  );
}
